// ============================================================================
// CHARGE TYPE SERVICE
// ============================================================================
// Server-side paged list (PagedResult -> PaginatedResponse), CRUD, and the
// active lookup that charge pickers use.

import { get, post, put, del } from '../client/axiosClient';
import { chargeTypeEndpoints, CHARGE_TYPE_BASE_PATH } from '../endpoints/chargeType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, EmptyResponse, ID } from '../types';
import type {
  ChargeType,
  ChargeTypeSearchRequest,
  CreateChargeTypeRequest,
  UpdateChargeTypeRequest,
} from '../types/chargeType.types';

class ChargeTypeService {
  async getAll(params?: ChargeTypeSearchRequest): Promise<PaginatedResponse<ChargeType>> {
    // Raw path, not chargeTypeEndpoints.list(): buildEndpointWithQuery adds the version prefix itself.
    const endpoint = params ? buildEndpointWithQuery(CHARGE_TYPE_BASE_PATH, params) : chargeTypeEndpoints.list();
    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw.data ?? {};
    return {
      success: raw.success,
      message: raw.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 20,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  /** Active charge types, in display order. manualOnly = those Front Office may post by hand. */
  async getLookup(manualOnly = false): Promise<ChargeType[]> {
    const endpoint = manualOnly ? `${chargeTypeEndpoints.lookup()}?manualOnly=true` : chargeTypeEndpoints.lookup();
    const response = await get<ApiResponse<ChargeType[]>>(endpoint);
    return response.data?.data ?? [];
  }

  async getById(id: ID): Promise<ApiResponse<ChargeType>> {
    const response = await get<ApiResponse<ChargeType>>(chargeTypeEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateChargeTypeRequest): Promise<ApiResponse<ChargeType>> {
    const response = await post<ApiResponse<ChargeType>>(chargeTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateChargeTypeRequest): Promise<ApiResponse<ChargeType>> {
    const response = await put<ApiResponse<ChargeType>>(chargeTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(chargeTypeEndpoints.delete(id), { params: { rowVersion } });
    return response.data;
  }

  async isCodeAvailable(code: string): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(chargeTypeEndpoints.validateCode(encodeURIComponent(code)));
    return Boolean(response.data?.data);
  }
}

export const chargeTypeService = new ChargeTypeService();
export default chargeTypeService;
